import { createTheme } from '@mui/material/styles';
import { THEME_MARITIME, THEME_BLUE, MARITIME, THEME_CONSTANTS } from './colors'; //THEME_DARK, THEME_BLUE, 

// *** The active theme selection - change this one line to switch themes ***
const ACTIVE_THEME = THEME_MARITIME;

const customTheme = createTheme({
    palette: {
        primary: ACTIVE_THEME.palette.primary,
        secondary: ACTIVE_THEME.palette.secondary,
        background: ACTIVE_THEME.palette.background,
        text: ACTIVE_THEME.palette.text,
        // Maintain orange as a custom palette color for backward compatibility
        orange: {
            light: ACTIVE_THEME.palette.secondary.light,
            main: ACTIVE_THEME.components?.MuiButton?.styleOverrides?.contained?.backgroundColor || ACTIVE_THEME.palette.secondary.main,
            dark: ACTIVE_THEME.palette.secondary.dark,
        },
        // Add custom colors - explicitly defined with fallbacks
        customColors: {
            linkOrange: ACTIVE_THEME.palette?.customColors?.linkOrange || THEME_CONSTANTS.LINK_ORANGE,
            highlight: {
                lightest: ACTIVE_THEME.palette?.customColors?.highlight?.lightest || MARITIME.LIGHT_BLUE,
                lighter: ACTIVE_THEME.palette?.customColors?.highlight?.lighter || MARITIME.SKY_BLUE,
                medium: ACTIVE_THEME.palette?.customColors?.highlight?.medium || MARITIME.CLEAR_BLUE,
                dark: ACTIVE_THEME.palette?.customColors?.highlight?.dark || MARITIME.STEEL_BLUE,
                darker: ACTIVE_THEME.palette?.customColors?.highlight?.darker || MARITIME.NAVY
            },
            result: ACTIVE_THEME.palette?.customColors?.result || MARITIME.WHITE,
            resultAlt: ACTIVE_THEME.palette?.customColors?.resultAlt || MARITIME.LIGHT_GRAY,
            buttonBackground: ACTIVE_THEME.palette?.customColors?.buttonBackground || MARITIME.ORANGE,
            buttonHover: ACTIVE_THEME.palette?.customColors?.buttonHover || MARITIME.DEEP_ORANGE,
            appBarBackground: ACTIVE_THEME.palette?.customColors?.appBarBackground || MARITIME.STEEL_BLUE,
            toggleSelected: ACTIVE_THEME.palette?.customColors?.toggleSelected || THEME_CONSTANTS.WHITE,
            toggleSelectedText: ACTIVE_THEME.palette?.customColors?.toggleSelectedText || MARITIME.STEEL_BLUE,
            toggleHover: ACTIVE_THEME.palette?.customColors?.toggleHover || MARITIME.STEEL_BLUE,
            toggleHoverBackground: ACTIVE_THEME.palette?.customColors?.toggleHoverBackground || MARITIME.LIGHT_BLUE,
            toggleUnselected: ACTIVE_THEME.palette?.customColors?.toggleUnselected || MARITIME.CLEAR_BLUE,
        }
    },
    typography: {
        fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
        fontSize: 17, // Base font size for body text (1.063rem)
        // H1 - Main headings (40px / 2.5rem desktop, scales down on mobile via components)
        h1: {
            fontSize: '2.5rem', // 40px
            fontWeight: 700,
            lineHeight: 1.2,
            color: MARITIME.NAVY,
        },
        // H2 - Section headings (32px / 2rem desktop for strong visual presence)
        h2: {
            fontSize: '2rem', // 32px - increased for better prominence
            fontWeight: 600,
            lineHeight: 1.2,
            color: MARITIME.NAVY,
        },
        // H3 - Subsection headings (24px / 1.5rem desktop)
        h3: {
            fontSize: '1.5rem', // 24px - increased for better hierarchy
            fontWeight: 600,
            lineHeight: 1.2,
            color: MARITIME.NAVY,
        },
        // H4 - Minor headings (20px / 1.25rem)
        h4: {
            fontSize: '1.25rem', // 20px
            fontWeight: 600,
            lineHeight: 1.2,
            color: ACTIVE_THEME.typography?.h4?.color || MARITIME.NAVY,
        },
        // H5 and H6 for completeness
        h5: {
            fontSize: '1.125rem', // 18px
            fontWeight: 600,
            lineHeight: 1.2,
            color: MARITIME.NAVY,
        },
        h6: {
            fontSize: '1rem', // 16px
            fontWeight: 600,
            lineHeight: 1.2,
            color: ACTIVE_THEME.typography?.h6?.color || MARITIME.NAVY,
        },
        // Body text (17px / 1.063rem base)
        body1: {
            fontSize: '1.063rem', // 17px
            lineHeight: 1.5,
            color: MARITIME.DARK_GRAY,
        },
        body2: {
            fontSize: '0.938rem', // 15px
            lineHeight: 1.5,
            color: MARITIME.DARK_GRAY,
        },
        // Custom section title variant
        sectionTitle: {
            fontSize: '2rem', // 32px (same as H2 - for section headings)
            fontWeight: 600,
            lineHeight: 1.2,
            color: ACTIVE_THEME.typography?.sectionTitle?.color || MARITIME.NAVY,
            marginBottom: '1.5rem',
            textAlign: 'center',
        },
        // Footer header variant
        footerHeader: {
            fontSize: '0.875rem', // 14px (slightly larger than before for readability)
            fontWeight: 600,
            textDecoration: 'underline',
            marginBottom: '0.5rem',
            lineHeight: 1.5,
            color: ACTIVE_THEME.typography?.footerHeader?.color || MARITIME.SKY_BLUE
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    minHeight: '48px', // Minimum touch target height
                    paddingLeft: '16px', // Minimum horizontal padding
                    paddingRight: '16px',
                    // Add subtle box-shadow on hover
                    '&:hover': {
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                },
                contained: {
                    backgroundColor: ACTIVE_THEME.palette?.customColors?.buttonBackground || MARITIME.ORANGE,
                    '&:hover': {
                        backgroundColor: ACTIVE_THEME.palette?.customColors?.buttonHover || MARITIME.DEEP_ORANGE,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                },
                // Ensure text buttons also meet touch target size
                text: {
                    minHeight: '48px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                },
                // Outlined buttons
                outlined: {
                    minHeight: '48px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                },
            },
        },
        // Icon buttons need 48x48px minimum
        MuiIconButton: {
            styleOverrides: {
                root: {
                    minWidth: '48px',
                    minHeight: '48px',
                    padding: '12px', // Ensures icon is centered with proper touch area
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 8px rgba(10, 35, 66, 0.1)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: ACTIVE_THEME.palette?.customColors?.appBarBackground || MARITIME.STEEL_BLUE
                }
            }
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    minHeight: '48px', // Touch target size for toggle buttons
                    color: ACTIVE_THEME.palette?.customColors?.toggleSelectedText || MARITIME.DARK_GRAY,
                    '&.Mui-selected': {
                        backgroundColor: ACTIVE_THEME.palette?.customColors?.toggleSelected || MARITIME.NAVY,
                        color: ACTIVE_THEME.palette?.customColors?.toggleSelectedText || MARITIME.WHITE,
                        fontWeight: 'bold',
                        '&:hover': {
                            backgroundColor: ACTIVE_THEME.palette?.customColors?.toggleHover || MARITIME.STEEL_BLUE,
                        },
                    },
                    '&:hover': {
                        backgroundColor: ACTIVE_THEME.palette?.customColors?.toggleHoverBackground || MARITIME.LIGHT_BLUE
                    }
                },
            },
        },
        // Menu items need proper touch targets too
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    minHeight: '48px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                },
            },
        },
        // Text fields for forms - mobile-friendly specifications
        MuiTextField: {
            defaultProps: {
                InputProps: {
                    sx: {
                        minHeight: '48px', // Proper touch target
                        fontSize: '1rem', // 16px minimum to prevent zoom on mobile
                    },
                },
                InputLabelProps: {
                    sx: {
                        fontSize: '1rem', // 16px for labels
                    },
                },
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        minHeight: '48px',
                        '& input': {
                            fontSize: '1rem', // 16px minimum
                            padding: '14px', // 12-16px padding range
                        },
                        '& textarea': {
                            fontSize: '1rem', // 16px minimum
                            padding: '14px',
                        },
                    },
                },
            },
        },
        // Select fields also need proper sizing
        MuiSelect: {
            styleOverrides: {
                root: {
                    minHeight: '48px',
                },
                select: {
                    fontSize: '1rem', // 16px minimum
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                },
            },
        },

        // Add any additional component overrides from the active theme
        //...(ACTIVE_THEME.components || {})
    },
});

// Export the active theme name for reference elsewhere
//export const ACTIVE_THEME_NAME = 'THEME_MARITIME';

export default customTheme;
